import React, { useState, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createTag, updateTag } from '../services/api';
import { useTagStore, notifyTagsChanged } from '../stores/tagStore';
import { Input, CustomSelect, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter, FormFieldWithError } from '@redpanda/ui';
import ColorPickerField from './forms/ColorPickerField';
import { toast } from 'react-hot-toast';

interface TagDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tag?: {
    id: string;
    name: string;
    category: string;
    color: string;
  };
}

interface TagFormData {
  name: string;
  category: string;
  color: string;
}

const TagDialog: React.FC<TagDialogProps> = ({
  isOpen,
  onClose,
  tag,
}) => {
  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<TagFormData>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  const { addTag, updateTag: updateTagInStore } = useTagStore();
  const { t } = useTranslation();


  const isEditMode = !!tag;
  const watchedColor = watch('color', '#8B5CF6');

  // 當對話框打開或標籤變化時，設置表單值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (tag) {
        // 編輯模式：設置現有標籤的值
        reset({
          name: tag.name,
          category: tag.category,
          color: tag.color
        });
      } else {
        // 新增模式：設置默認值
        reset({
          name: '',
          category: 'CUSTOM',
          color: '#8B5CF6'
        });
      }
    }
  }, [isOpen, tag, reset]);

  const categoryOptions = [
    { value: 'INDUSTRY', label: t('common.categories.industry') },
    { value: 'SIZE', label: t('common.categories.size') },
    { value: 'STATUS', label: t('common.categories.status') },
    { value: 'CUSTOM', label: t('common.categories.custom') },
  ];

  const onSubmit = async (data: TagFormData) => {
    setLoading(true);
    setSubmitError(null);

    try {
      const payload = {
        name: data.name.trim(),
        category: data.category,
        color: data.color,
      };

      if (isEditMode && tag) {
        const response = await updateTag(tag.id, payload);
        updateTagInStore(tag.id, response.data);
        toast.success(t('tags.edit.success', { ns: 'pages' }));
      } else {
        const response = await createTag(payload);
        addTag(response.data);
        toast.success(t('tags.add.success', { ns: 'pages' }));
      }

      onClose();
      reset();
      notifyTagsChanged(); // 標籤選擇器重新載入全部標籤
    } catch (error: any) {
      console.error('Tag submission failed:', error);

      // 處理各種錯誤情況
      let errorMessage = t('common.status.error');

      if (error.response?.data?.code === 'TAG_ALREADY_EXISTS') {
        // 標籤名稱已存在
        const tagName = error.response.data.error || '';
        errorMessage = isEditMode
          ? t('tags.edit.tagAlreadyExists', { tagName, ns: 'pages' })
          : t('tags.add.tagAlreadyExists', { tagName, ns: 'pages' });
      } else if (error.response?.data?.code === 'TAG_NOT_FOUND') {
        errorMessage = t('tags.edit.tagNotFound', { ns: 'pages' });
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        // 網路或其他錯誤
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset();
    setSubmitError(null);
  };

  if (!isOpen) return null;

  return (
    <DialogShell onClose={handleClose} closeDisabled={loading}>
      <DialogHeader title={isEditMode ? t('tags.edit.title', { ns: 'pages' }) : t('tags.add.title', { ns: 'pages' })} onClose={handleClose} closeDisabled={loading} closeLabel={t('common.actions.close')} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          <FormFieldWithError
            label={t('tags.add.form.name', { ns: 'pages' })}
            required={true}
            error={errors.name?.message}
            htmlFor="name"
          >
            <Input
              type="text"
              id="name"
              {...register('name', { 
                required: t('common.forms.validation.required'),
                minLength: { value: 1, message: t('common.forms.validation.required') },
                maxLength: { value: 50, message: t('common.forms.validation.maxLength', { length: 50 }) }
              })}
              variant={errors.name ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('common.forms.labels.category')}
            error={errors.category?.message}
            htmlFor="category"
          >
            <CustomSelect
              id="category"
              {...register('category', { required: t('common.forms.validation.required') })}
              value={watch('category')}
              onChange={(value) => setValue('category', value)}
              options={categoryOptions}
              variant={errors.category ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('tags.add.form.color', { ns: 'pages' })}
            error={errors.color?.message}
            htmlFor="color"
          >
            <ColorPickerField
              id="color"
              value={watchedColor}
              onChange={(value) => setValue('color', value)}
            />
          </FormFieldWithError>

          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {submitError}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
            disabled={loading}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            loading={loading}
            isEditMode={isEditMode}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default TagDialog;